import $ from '@/util/jQuery';

import { toCamelCase } from '@/util/String';
import { getTeamAbbreviation } from '@/util/Parsing/Teams';

const FORWARD = 'F';
const PP_FORWARD = 'PPF';
const PK_FORWARD = 'PKF';
const FOUR_ON_FOUR_FORWARD = 'FONFF';
const EXTRA_FORWARD = 'XF';

const DEFENSE = 'D';
const PP_DEFENSE = 'PPD';
const PK_DEFENSE = 'PKD';
const FOUR_ON_FOUR_DEFENSE = 'FONFD';
const EXTRA_DEFENSE = 'XD';

const GOALIE = 'G';

const PK3_SKATER = 'PK3';
const LAST_MINUTE_OFFENSIVE = 'LMO';
const LAST_MINUTE_DEFENSIVE = 'LMD';
const PENALTY_SHOTS = 'PS';
const SCRATCHES = 'SCR';

/**
  Given a standard STHS HTML Output, will pull the players in the game
  @params html - The STHS HTML Output
*/
export function getPlayersFromHTMLOutput(html){
  const dictionary = {};

  $(html)
    .find("#tabmain3")
    .find('.STHSBoxScore_TeamLine')
    .each((index, el) => {
      const team = getTeamAbbreviation(el.textContent);

      $(el)
        .nextUntil(".STHSBoxScore_TeamLine")
        .filter(".STHSBoxScore_Lines_Table")
        .each((_, tableEl) => {
          const tableName = $(tableEl).find('tbody tr th').first().text();

          if(tableName === 'Time') {
            return;
          }

          const position = getPositionFromHTML(tableName);
          const trs = $.makeArray($(tableEl).find('tbody tr'));
          let players = [];

          switch(position) {
            case FORWARD:
            case PP_FORWARD:
              players = parseStandardTable(trs, position, 4);
              break;
            case DEFENSE:
            case PP_DEFENSE:
            case PK_FORWARD:
            case PK_DEFENSE:
            case FOUR_ON_FOUR_FORWARD:
            case FOUR_ON_FOUR_DEFENSE:
              players = parseStandardTable(trs, position, 3);
              break;
            case PK3_SKATER:
              players = parse3PKTable(trs, position);
              break;
            case LAST_MINUTE_OFFENSIVE:
            case LAST_MINUTE_DEFENSIVE:
              players = parseLastMinuteTable(trs, position);
              break;
            case EXTRA_FORWARD:
            case EXTRA_DEFENSE:
              players = parseExtrasTable(trs, position);
              break;
            case PENALTY_SHOTS:
            case GOALIE:
              players = parseListTable(trs, position);
              break;
            case SCRATCHES:
              // DO NOTHING RIGHT NOW
              break;
            default:
              break;
          }
          
          // Add players to dictionary
          players.forEach(player => {
            addPositionToPlayer(dictionary, player.name, team, position, player.line);
          })
        })
    })
  
  return dictionary;
}

function parseListTable(rows, position) {
  const list = Array.from(rows[1].children).map(td => td.textContent);
  return list[0].split(',').map((player, id) => ({
    name: player.trim(),
    line:`${position}${id + 1}`
  }))
}

function parseExtrasTable(rows, position) {
  const situations = rows.filter((_, id) => id > 1)
    .map(tr => Array.from(tr.children).map(td => td.textContent))

  return situations.reduce((arr, situation) => {
    situation.forEach((type, tId) => {
      const posArr = ['EV', 'PP', 'PK'];
      const players = type.split(',');
      
      players.forEach((player,idx) => {
        arr.push({
          name: player.trim(),
          line:`${position}-${posArr[tId]}-${idx + 1}`
        })
      })
    })

    return arr;
  }, [])
}

function parseLastMinuteTable(rows, position) {
  const lines = rows.filter((_, id) => id > 1)
    .map(tr => Array.from(tr.children)
      .map(td => td.textContent)
    )

  return lines.reduce((arr, line) => {
    line.forEach((player, idx) => {
      const posArr = ['LW', 'C', 'RW', 'D', 'D'];

      arr.push({
        name: player.trim(),
        line:`${position}-${posArr[idx]}`
      })
    })

    return arr;
  }, [])
}

function parse3PKTable(rows, position) {
  const lines = rows.filter((_, id) => id > 1)
    .map(tr => Array.from(tr.children)
      .filter((_, tdId) => [0, 1, 8, 9].includes(tdId))
      .map(td => td.textContent)
    )

  return lines.reduce((arr, line) => {
    const [lineNo, ...players] = line;

    players.forEach((player, id) => {
      arr.push({
        name: player.trim(),
        line:`${position}${id > 0 ? 'D' : 'F'}-${lineNo}`
      })
    })

    return arr;
  }, [])
}


function parseStandardTable(rows, position, columns) {
  const lines = rows.filter((tr, id) => id > 1)
    .map(tr => Array.from(tr.children)
      .filter((_, tdId) => tdId < columns)
      .map(td => td.textContent)
    )

  return lines.reduce((arr, line) => {
    const [lineNo, ...players] = line;

    players.forEach(player => {
      arr.push({
        name: player.trim(),
        line:`${position}-${lineNo}`
      })
    })

    return arr;
  }, [])
}


/**
  Given a standard STHS HTML Output, will pull the player and goalie stats in the game
  @params html - The STHS HTML Output
*/
export function getPlayerStatsFromHTMLOutput(html){
  const skaters = getSkaterStatsFromHTMLOutput(html);
  const goalies = getGoalieStatsFromHTMLOutput(html);

  return {
    skaters,
    goalies,
  }
}

/**
  Given a standard STHS HTML Output, will pull the player stats in the game
  @params html - The STHS HTML Output
*/
export function getSkaterStatsFromHTMLOutput(html){
  const dictionary = {};

  $(html)
    .find("#tabmain2")
    .find('.STHSBoxScore_PlayerStatTitle')
    .each((_, el) => {

      const team = getTeamAbbreviation(el.textContent);

      $(el)
        .next()
        .find("tr")
        .filter(idx => idx > 0)
        .each((_, tableEl) => {
          const statLine = {
            team
          };
          
          // Loop TDS
          $(tableEl).children('td').each((tdId, tdEl) => {
            const text = tdEl.innerText;
            let minutes, seconds, toi;

            switch(tdId) {
              // Name
              case 0:
                statLine.id = toCamelCase(text.replace(/[^\w\s]/gi, ""));;
                statLine.name = text;
                break;
              // POS
              case 1:
                break;
              // Goals
              case 2:
                statLine.goals = parseInt(text);
                break;
              // Assists
              case 3:
                statLine.assists = parseInt(text);
                break;
              // Points
              case 4:
                statLine.points = parseInt(text);
                break;
              // +/-
              case 5:
                statLine.plusMinus = text;
                break;
              // PIM
              case 6:
                statLine.pims = parseInt(text);
                break;
              // Hit
              case 7:
                statLine.hits = parseInt(text);
                break;
              // Shots
              case 8:
                statLine.shots = parseInt(text);
                break;
              // Shots Blocked
              case 9:
                statLine.shotsBlocked = parseInt(text);
                break;
              // TOI
              case 10:
                [minutes, seconds] = text.split(':');
                toi = parseInt(minutes) * 60 + parseInt(seconds);
                statLine.toi = toi;
                break;
              // PP TOI
              case 11:
                [minutes, seconds] = text.split(':');
                toi = parseInt(minutes) * 60 + parseInt(seconds);
                statLine.pptoi = toi;
                break;
              // PP Shots
              case 12:
                statLine.ppShots = parseInt(text);
                break;
              // PK TOI
              case 13:
                [minutes, seconds] = text.split(':');
                toi = parseInt(minutes) * 60 + parseInt(seconds);
                statLine.pktoi = toi;
                break;
              // PK Shots
              case 14:
                statLine.pkShots = parseInt(text);
                break;
              // Faceoff Percent
              case 15:
                statLine.faceoffsPct = text;
                break;
              // Faceoff Taken
              case 16:
                statLine.faceoffsTaken = parseInt(text);
                break;
              default:
                break;
            }
          })

          // Calculate faceoffs won
          const pct = parseInt(statLine.faceoffsPct.replace('%', ''))
          statLine.faceoffsWon = Math.floor(statLine.faceoffsTaken * (pct / 100));
          
          // Add to dictionary
          dictionary[statLine.id] = statLine;
        })
    })
  
  return dictionary;
}





/**
  Given a standard STHS HTML Output, will pull the goalie stats in the game
  Ex: Ondrej Pavelec (STL), 30 saves from 32 shots - (0.938), 40:15 minutes
  Ex: Marc-Andre Fleury (STL), 8 saves from 12 shots - (0.667), L, 4-4-0, 19:45 minutes
  @params html - The STHS HTML Output
*/
function getGoalieStatsFromHTMLOutput(html){
  const goalies = {};

  const goalieStats = $(html)
    .find(".STHSBoxScore_PeriodPerPeriod_Header")
    .filter((_, value) => value.innerText === 'Goalie Stats')
    .map((_, item) => $(item).parent().next('tr').children('td')[0].innerHTML)[0]
    .split('<br>')
    .map(text => text.trim())
    .filter(text => text.length > 0)

  goalieStats.forEach(stat => {
    let [ nameAndTeam, savesShots, ...other ] = stat.split(', ');

    // Create player Id
    const [name, team] = nameAndTeam.split(' (');
    const id = toCamelCase(name.replace(/[^\w\s]/gi, ""));

    goalies[id] = {
      id,
      name,
      team: getTeamAbbreviation(team.replace(')', ''))
    }

    // Saves and Shots
    let [saves, shots] = savesShots.split(' saves from ');
    shots = shots.split(' ')[0];
    goalies[id].saves = parseInt(saves);
    goalies[id].shots = parseInt(shots);

    // The goalie got a win or a loss
    if(other.length > 1){
      const decision = other[0];
      const [wins, loss, otl] = other[1].split('-');

      // Add them in
      goalies[id] = {
        ...goalies[id],
        decision,
        wins,
        loss,
        otl,
      }
      
      // Take them out of the other array
      other.splice(0, 2);

      // TOI
      const [minutes, seconds] = other[0].split(' ')[0].split(':');
      const toi = parseInt(minutes) * 60 + parseInt(seconds);
      goalies[id].toi = toi;
    }
  })

  return goalies;
}

/**
  Will add a new standard position to the player dictionary provided or add the player in
*/
function addPositionToPlayer(dictionary, player, team, position, line){
    const id = toCamelCase(player.replace(/[^\w\s]/gi, ""));

    if(dictionary[id]){
      dictionary[id].lines.push(line);
      return;
    }

    dictionary[id] = {
      id,
      team,
      name: player,
      position,
      lines: [line],
    }
}

/**
  Given a standard STHS HTML Output,
  will pull the player position from the text
*/
function getPositionFromHTML(text){
  if(text.includes('5 vs 5 Forward')){
    return FORWARD;
  }

  if(text.includes('5 vs 5 Defense')){
    return DEFENSE;
  }

  if(text.includes('Power Play Forward')){
    return PP_FORWARD;
  }

  if(text.includes('Power Play Defense')){
    return PP_DEFENSE;
  }

  if(text.includes('Penalty Kill 4 Players Forward')){
    return PK_FORWARD;
  }

  if(text.includes('Penalty Kill 4 Players Defense')){
    return PK_DEFENSE;
  }

  if(text.includes('Penalty Kill 3 Players')){
    return PK3_SKATER;
  }

  if(text.includes('4 vs 4 Forward')){
    return FOUR_ON_FOUR_FORWARD;
  }

  if(text.includes('4 vs 4 Defense')){
    return FOUR_ON_FOUR_DEFENSE;
  }

  if(text.includes('Last Minutes Offensive')){
    return LAST_MINUTE_OFFENSIVE;
  }

  if(text.includes('Last Minutes Defensive')){
    return LAST_MINUTE_DEFENSIVE;
  }

  if(text.includes('Goalie')){
    return GOALIE;
  }

  if(text.includes('Extra Forwards')){
    return EXTRA_FORWARD;
  }

  if(text.includes('Extra Defensemen')){
    return EXTRA_DEFENSE;
  }

  if(text.includes('Penalty Shots')){
    return PENALTY_SHOTS;
  }

  if(text.includes('Scratches')){
    return SCRATCHES;
  }

  debugger;
  // WHY YOU GET HERE?
}

export default {
  getPlayersFromHTMLOutput,
  getPlayerStatsFromHTMLOutput
}